var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { create } from "zustand";
import { getStudioContentURL, getAuthenticatedStudioURL } from "../ContentAPI";
import { getAPIEndpoint } from "../API";
import { getAvailableModelsForJobType, } from "../interface";
export const JOB_TYPES = [
    {
        id: "text-to-image",
        title: "Text-to-Image",
        description: "Generate images from text using AI",
        image: "/img/painter-cat.jpg",
        available: true,
    },
    {
        id: "image-to-image",
        title: "Image-to-Image",
        description: "Transform existing images with AI",
        image: "/img/girl-w-cat.jpg",
        available: true,
    },
    {
        id: "text-to-video",
        title: "Text-to-Video",
        description: "Create videos from text descriptions",
        image: "/img/motorcycle-cat-fast.jpg",
        available: false,
    },
    {
        id: "image-to-video",
        title: "Image-to-Video",
        description: "Transform images into videos",
        image: "/img/video-cat.jpg",
        available: false,
    },
];
export const getAvailableModels = (jobType) => {
    return getAvailableModelsForJobType(jobType);
};
export const getJobTypeConfig = (jobType) => {
    return JOB_TYPES.find((type) => type.id === jobType);
};
export const getStudioJobThumbnailUrl = (job, bearer) => {
    if (job.jobStatus === "completed" &&
        job.jobFiles &&
        job.jobFiles.length > 0) {
        // Use the first result file as thumbnail
        const firstResultFile = job.jobFiles[0];
        // Check if it's already a full path (starts with u/ or tmp/)
        if (firstResultFile.startsWith("u/") ||
            firstResultFile.startsWith("tmp/")) {
            return getAuthenticatedStudioURL(firstResultFile, bearer);
        }
        else {
            // Legacy format - construct the studio path using new u/ prefix
            return getStudioContentURL(job.jobOwnerId, job.jobId, firstResultFile, bearer);
        }
    }
    return undefined;
};
export const getStudioJobDisplayTitle = (job) => {
    return job.title;
};
export const getJobStatusColor = (status) => {
    switch (status) {
        case "pending":
            return "#FFA726"; // Orange
        case "processing":
            return "#42A5F5"; // Blue
        case "completed":
            return "#66BB6A"; // Green
        case "failed":
            return "#EF5350"; // Red
        default:
            return "#757575"; // Gray
    }
};
export const getJobStatusIcon = (status) => {
    switch (status) {
        case "pending":
            return "schedule";
        case "processing":
            return "autorenew";
        case "completed":
            return "check_circle";
        case "failed":
            return "error";
        default:
            return "help";
    }
};
export const formatJobAge = (createdAt) => {
    const now = Date.now();
    const diffMs = now - createdAt;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffMinutes < 1) {
        return "just now";
    }
    else if (diffMinutes < 60) {
        return `${diffMinutes}m ago`;
    }
    else if (diffHours < 24) {
        return `${diffHours}h ago`;
    }
    else {
        return `${diffDays}d ago`;
    }
};
export const getJobResultImageUrl = (job) => {
    return getStudioJobThumbnailUrl(job);
};
export const useStudioJobStore = create((set, get) => ({
    jobs: [],
    loading: false,
    loadingMore: false,
    error: undefined,
    hasMoreJobs: false,
    nextPageStartKey: null,
    recentMediaFiles: [],
    serverStatus: null,
    loadJobs: (auth, cacheBusterStore, logger) => __awaiter(void 0, void 0, void 0, function* () {
        logger.log("StudioJobStore: loadJobs");
        set({ loading: true, error: undefined });
        try {
            // Add type checking for sequence
            const sequenceValue = auth.sequence || 0;
            if (typeof sequenceValue !== "number") {
                logger.error("StudioJobStore: SEQUENCE TYPE ERROR - auth.sequence is " +
                    typeof sequenceValue +
                    " with value: " +
                    JSON.stringify(sequenceValue));
            }
            const input = {
                operation: "listJobsByOwner",
                sequence: sequenceValue,
            };
            logger.log("StudioJobStore: Sending request with sequence: " +
                JSON.stringify(sequenceValue) +
                " (type: " +
                typeof sequenceValue +
                ")");
            const response = yield fetch(getAPIEndpoint() + "studio", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Basic ${auth.bearer}`,
                },
                body: JSON.stringify(input),
            });
            const result = yield response.json();
            if (!response.ok) {
                // Check if this is a sequence number error - if so, don't treat it as a fatal error
                if (result.error && result.error.includes("sequence number")) {
                    logger.log("StudioJobStore: Sequence number error - bearer/sequence updated, retrying will use correct values");
                    set({ loading: false, error: undefined });
                    return;
                }
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            // Always update bearer and sequence if provided, even on error responses
            if (result.bearer && result.sequence !== undefined) {
                logger.log("StudioJobStore: Updating auth with sequence: " +
                    JSON.stringify(result.sequence) +
                    " (type: " +
                    typeof result.sequence +
                    ")");
                auth.setBearerAndSequence(result.bearer, result.sequence, logger);
            }
            const jobs = result.jobs || [];
            set({
                jobs,
                hasMoreJobs: result.hasMoreJobs || false,
                nextPageStartKey: result.nextPageStartKey || null,
                loading: false,
                error: undefined,
            });
            logger.log(`StudioJobStore: Loaded ${jobs.length} jobs, hasMore: ${result.hasMoreJobs}, nextKey: ${result.nextPageStartKey}`);
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Unknown error";
            logger.warn("StudioJobStore: Error loading jobs: " + errorMessage);
            set({ loading: false, error: undefined });
        }
    }),
    loadMoreJobs: (auth, cacheBusterStore, logger) => __awaiter(void 0, void 0, void 0, function* () {
        const { hasMoreJobs, nextPageStartKey, loadingMore, loading } = get();
        // Don't load more if already loading or no more jobs available
        if (loadingMore || loading || !hasMoreJobs || !nextPageStartKey) {
            logger.log(`StudioJobStore: loadMoreJobs skipped - loadingMore: ${loadingMore}, loading: ${loading}, hasMore: ${hasMoreJobs}, nextKey: ${nextPageStartKey}`);
            return;
        }
        logger.log(`StudioJobStore: loadMoreJobs with startKey: ${nextPageStartKey}`);
        set({ loadingMore: true, error: undefined });
        try {
            const sequenceValue = auth.sequence || 0;
            const input = {
                operation: "listJobsByOwner",
                sequence: sequenceValue,
                startKey: nextPageStartKey,
            };
            const response = yield fetch(getAPIEndpoint() + "studio", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Basic ${auth.bearer}`,
                },
                body: JSON.stringify(input),
            });
            const result = yield response.json();
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            // Update bearer and sequence if provided
            if (result.bearer && result.sequence !== undefined) {
                auth.setBearerAndSequence(result.bearer, result.sequence, logger);
            }
            const newJobs = result.jobs || [];
            const currentJobs = get().jobs;
            // Append new jobs to existing list (avoid duplicates)
            const existingJobIds = new Set(currentJobs.map((j) => j.jobId));
            const uniqueNewJobs = newJobs.filter((j) => !existingJobIds.has(j.jobId));
            set({
                jobs: [...currentJobs, ...uniqueNewJobs],
                hasMoreJobs: result.hasMoreJobs || false,
                nextPageStartKey: result.nextPageStartKey || null,
                loadingMore: false,
                error: undefined,
            });
            logger.log(`StudioJobStore: Loaded ${uniqueNewJobs.length} more jobs, total: ${currentJobs.length + uniqueNewJobs.length}, hasMore: ${result.hasMoreJobs}`);
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Unknown error";
            logger.warn("StudioJobStore: Error loading more jobs: " + errorMessage);
            // Disable pagination on error to prevent repeated failures
            set({ loadingMore: false, hasMoreJobs: false, nextPageStartKey: null });
        }
    }),
    createJob: (input, auth, cacheBusterStore, logger) => __awaiter(void 0, void 0, void 0, function* () {
        logger.log("StudioJobStore: createJob: " + JSON.stringify(input));
        const apiInput = {
            operation: "createJob",
            title: input.title,
            jobType: input.jobType,
            modelId: input.modelId,
            positivePrompt: input.positivePrompt,
            negativePrompt: input.negativePrompt,
            // Video parameters
            videoWidth: input.videoWidth,
            videoHeight: input.videoHeight,
            videoLength: input.videoLength,
            videoFPS: input.videoFPS,
            videoSeed: input.videoSeed,
            // Image-to-image parameters
            sourceImagePath: input.sourceImagePath,
            recaptchaToken: input.recaptchaToken,
            sequence: auth.sequence || 0,
        };
        const response = yield fetch(getAPIEndpoint() + "studio", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Basic ${auth.bearer}`,
            },
            body: JSON.stringify(apiInput),
        });
        const result = yield response.json();
        // Always update bearer and sequence if provided, even on error responses
        if (result.bearer && result.sequence !== undefined) {
            auth.setBearerAndSequence(result.bearer, result.sequence, logger);
        }
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        if (!result.job) {
            throw new Error("No job returned from create operation");
        }
        const newJob = Object.assign(Object.assign({}, result.job), { title: input.title });
        // Update local state
        const currentJobs = get().jobs;
        set({ jobs: [newJob, ...currentJobs] });
        logger.log("StudioJobStore: Job created: " + newJob.jobId);
        return newJob;
    }),
    getJob: (jobId, auth, cacheBusterStore, logger) => __awaiter(void 0, void 0, void 0, function* () {
        logger.log("StudioJobStore: getJob: " + jobId);
        const input = {
            operation: "getJob",
            jobId,
            sequence: auth.sequence || 0,
        };
        const response = yield fetch(getAPIEndpoint() + "studio", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Basic ${auth.bearer}`,
            },
            body: JSON.stringify(input),
        });
        const result = yield response.json();
        // Always update bearer and sequence if provided, even on error responses
        if (result.bearer && result.sequence !== undefined) {
            auth.setBearerAndSequence(result.bearer, result.sequence, logger);
        }
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        if (!result.job) {
            throw new Error("Job not found");
        }
        const job = result.job;
        // Update local state if job exists in current jobs
        const currentJobs = get().jobs;
        const updatedJobs = currentJobs.map((j) => (j.jobId === jobId ? job : j));
        if (updatedJobs.some((j) => j.jobId === jobId)) {
            set({ jobs: updatedJobs });
        }
        logger.log("StudioJobStore: Job retrieved: " + jobId);
        return job;
    }),
    refreshJob: (jobId, auth, cacheBusterStore, logger) => __awaiter(void 0, void 0, void 0, function* () {
        logger.log("StudioJobStore: refreshJob: " + jobId);
        yield get().getJob(jobId, auth, cacheBusterStore, logger);
    }),
    deleteJob: (jobId, auth, cacheBusterStore, logger) => __awaiter(void 0, void 0, void 0, function* () {
        logger.log("StudioJobStore: deleteJob: " + jobId);
        const input = {
            operation: "deleteJob",
            jobId,
            sequence: auth.sequence || 0,
        };
        const response = yield fetch(getAPIEndpoint() + "studio", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Basic ${auth.bearer}`,
            },
            body: JSON.stringify(input),
        });
        const result = yield response.json();
        // Always update bearer and sequence if provided, even on error responses
        if (result.bearer && result.sequence !== undefined) {
            auth.setBearerAndSequence(result.bearer, result.sequence, logger);
        }
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        // Remove job from local state
        const currentJobs = get().jobs;
        const updatedJobs = currentJobs.filter((j) => j.jobId !== jobId);
        set({ jobs: updatedJobs });
        logger.log("StudioJobStore: Job deleted: " + jobId);
    }),
    listRecentMedia: (auth, cacheBusterStore, logger) => __awaiter(void 0, void 0, void 0, function* () {
        logger.log("StudioJobStore: listRecentMedia");
        try {
            const input = {
                operation: "listRecentMedia",
                sequence: auth.sequence || 0,
            };
            const response = yield fetch(getAPIEndpoint() + "studio", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Basic ${auth.bearer}`,
                },
                body: JSON.stringify(input),
            });
            const result = yield response.json();
            // Always update bearer and sequence if provided, even on error responses
            if (result.bearer && result.sequence !== undefined) {
                auth.setBearerAndSequence(result.bearer, result.sequence, logger);
            }
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            const recentMediaFiles = result.recentMediaFiles || [];
            set({ recentMediaFiles });
            logger.log(`StudioJobStore: Loaded ${recentMediaFiles.length} recent media files`);
            return recentMediaFiles;
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Unknown error";
            logger.error("StudioJobStore: Error loading recent media: " + errorMessage);
            set({ error: errorMessage });
            return [];
        }
    }),
    getServerStatus: (auth, cacheBusterStore, logger) => __awaiter(void 0, void 0, void 0, function* () {
        logger.log("StudioJobStore: getServerStatus");
        console.log("StudioJobStore: getServerStatus called");
        try {
            const input = {
                operation: "getServerStatus",
                sequence: auth.sequence || 0,
            };
            console.log("StudioJobStore: Making getServerStatus request with input:", input);
            const response = yield fetch(getAPIEndpoint() + "studio", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Basic ${auth.bearer}`,
                },
                body: JSON.stringify(input),
            });
            const result = yield response.json();
            console.log("StudioJobStore: getServerStatus response:", {
                ok: response.ok,
                status: response.status,
                result: result,
            });
            // Always update bearer and sequence if provided, even on error responses
            if (result.bearer && result.sequence !== undefined) {
                auth.setBearerAndSequence(result.bearer, result.sequence, logger);
            }
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            if (result.serverStatus) {
                console.log("StudioJobStore: Setting server status:", result.serverStatus);
                set({ serverStatus: result.serverStatus });
                logger.log(`StudioJobStore: Server status updated - ${result.serverStatus.jobsInQueue} jobs in queue`);
            }
            else {
                console.log("StudioJobStore: No server status in response:", result);
            }
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Unknown error";
            console.error("StudioJobStore: Error getting server status:", error);
            logger.error("StudioJobStore: Error getting server status: " + errorMessage);
        }
    }),
    clearJobs: () => {
        set({
            jobs: [],
            loading: false,
            loadingMore: false,
            error: undefined,
            hasMoreJobs: false,
            nextPageStartKey: null,
            recentMediaFiles: [],
            serverStatus: null,
        });
    },
    setError: (error) => {
        set({ error });
    },
}));
